import { useState, useRef, useEffect } from "react";
import * as Icons from "lucide-react";

/**
 * Inline editable text label for clip / reel cards.
 *
 * - Renders as plain text by default with a tiny pencil icon on hover.
 * - Click the pencil (or click the text twice) → edits in place.
 * - Enter or blur saves; Escape cancels.
 * - Caller passes an `onSave(newValue)` async function; component shows a
 *   subtle saving indicator and reverts on error.
 *
 * Designed to be dropped into existing card layouts without breaking them.
 */
export default function EditableLabel({
  value,
  onSave,
  className = "",
  inputClassName = "",
  placeholder = "Untitled",
  maxLength = 140,
  testId,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value || "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === (value || "").trim()) {
      setEditing(false);
      setDraft(value || "");
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      setDraft(value || "");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 min-w-0">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          maxLength={maxLength}
          onChange={(e) => setDraft(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") commit();
            else if (e.key === "Escape") {
              setEditing(false);
              setDraft(value || "");
            }
          }}
          disabled={saving}
          className={`bg-white/10 border border-white/20 rounded px-2 py-0.5 text-white outline-none focus:border-white/40 min-w-0 flex-1 ${inputClassName}`}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        {saving && <Icons.Loader2 size={11} className="animate-spin text-zinc-400 flex-shrink-0" />}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className={`group/edit inline-flex items-center gap-1 hover:text-white text-left min-w-0 ${className}`}
      title="Click to rename"
      data-testid={testId}
    >
      <span className="truncate min-w-0">{value || placeholder}</span>
      <Icons.Pencil
        size={10}
        className="opacity-0 group-hover/edit:opacity-100 text-zinc-500 flex-shrink-0 transition-opacity"
      />
    </button>
  );
}
